// @flow
import Properties from "../../../Properties";
import { getInit, postInit, graphQLInit } from "../../global/types/constants";
import type { DispatchType } from "../../global/types/redux";
import { QuoteNode, PartNode, ProductSheetNode, DiscountsNode } from "../../account/types/GraphQlNodes";
import { apiFailCallbackHandle } from "../../account/containers/ErrosDisplay";
import {
  ADMIN_UPDATE_QUOTE_LIST,
  ADMIN_DELETE_QUOTE_ITEM,
  ADMIN_GET_QUOTE_COMPANY_LIST,
  ADMIN_UPDATE_PRODUCT_SHEET_LIST,
  ADMIN_GET_SHEET_COMPANY_LIST,
  ADMIN_UPDATE_PRODUCT_TIER,
  ADMIN_GET_QUOTE_LIST
} from "../../global/actions/actionNames";

const root = Properties.api_root;

export const fetchAdminQuoteList = (callback?: Function): * => (
  dispatch: DispatchType
): * => {
  const graphQl = `
  {
    allQuotes {
      ${QuoteNode}
    }
  }
  `;
  return fetch(`${root}/auth/v2/billing/graphql`, graphQLInit(graphQl))
    .then(response => response.json())
    .then(json => {
      dispatch({ type: ADMIN_GET_QUOTE_LIST, quotes: json.data.allQuotes });
      callback && callback(json.data.allQuotes);
    });
};

export const deleteQuote = (
  quote_id: string,
  doneCallback: Function,
  failCallBack: Function
): * => (dispatch: DispatchType): * => {
  const graphQl = `
  mutation {
    deleteQuote(
      id: "${quote_id}"
    ){
      ok
      errors {
        field
        message
      }
    }
  }
  `;
  return fetch(`${root}/auth/v2/billing/graphql`, graphQLInit(graphQl))
    .then(response => response.json())
    .then(json => {
      if (json.errors) {
        failCallBack && failCallBack(apiFailCallbackHandle(json.errors));
      } else {
        const result = json.data.deleteQuote;
        if (result.ok) {
          dispatch({ type: ADMIN_DELETE_QUOTE_ITEM, quoteId: quote_id });
          doneCallback && doneCallback();
        } else {
          failCallBack && failCallBack(apiFailCallbackHandle(result.error));
        }
      }
    })
    .catch(err => {
      failCallBack && failCallBack(apiFailCallbackHandle(err.message));
    });
};

export const fetchChildrenList = (
  companyId: string,
  callback?: Function
): * => (dispatch: DispatchType): * => {
  const graphQl = `
  {
    selectChildren(id: "${companyId}") {
      id
      uuid
      name
      companyType
      inheritAuthority
    }
  }
  `;
  return fetch(`${root}/auth/v2/billing/graphql`, graphQLInit(graphQl))
    .then(response => response.json())
    .then(json => {
      dispatch({
        type: ADMIN_GET_QUOTE_COMPANY_LIST,
        companyId: companyId,
        children: json.data.selectChildren
      });
      callback && callback(json.data.selectChildren);
    });
};

export const updateProductPart = (
  partNo: string,
  basePrice: number,
  minimum: number,
  maximum: number,
  description: string,
  notes: string,
  ranges: Array<*>,
  doneCallback: Function,
  failCallBack: Function
): * => (dispatch: DispatchType): * => {
  let priceRanges = "";
  ranges.map(
    (range: *): * => {
      priceRanges +=
        "{ minimumQuantity:" + range.minimum + ", price:" + range.price + "},";
    }
  );
  let maxNode = maximum === null ? "" : `maximumQuantity: ${maximum},`;
  const graphQl = `
  mutation {
    updateProductParts(
      partInput: {
        partNo: "${partNo}",
        basePrice: ${basePrice},
        minimumQuantity: ${minimum},
        ${maxNode}
        description: "${description}",
        notes: "${notes}",
        priceRanges: [${priceRanges}]
      }
    ){
      ok
      errors{
        ...on PartNotFound{
          message
          errorType
        }
      }
      part{${PartNode}}
    }
  }
  `;
  return fetch(`${root}/auth/v2/billing/graphql`, graphQLInit(graphQl))
    .then(response => response.json())
    .then(json => {
      if (json.errors) {
        failCallBack && failCallBack(apiFailCallbackHandle(json.errors));
      } else {
        const result = json.data.updateProductParts;
        if (result.ok) {
          doneCallback && doneCallback();
        } else {
          failCallBack && failCallBack(apiFailCallbackHandle(result.error));
        }
      }
    })
    .catch(err => {
      failCallBack && failCallBack(apiFailCallbackHandle(err.message));
    });
};

export const createProductSheet = (
  description: string,
  parts: Array<*>,
  doneCallback: Function,
  failCallBack: Function
): * => (dispatch: DispatchType): * => {
  let partList = "";
  parts.map(
    (part: *): * => {
      let priceRanges = "";
      part.ranges.map(
        (range: *): * => {
          priceRanges +=
            "{ minimumQuantity:" + range.minimum + ", price:" + range.price + "},";
        }
      );

      partList +=
        "{ partNo:\"" +
        part.partNo +
        "\", basePrice:" +
        part.price +
        ", minimumQuantity:" +
        part.minimum +
        ", " +
        (part.maximum === null
          ? ""
          : "maximumQuantity:" + part.maximum + ", ") +
        "priceRanges: [" + priceRanges + "]" +
        "},";
    }
  );

  const graphQl = `
  mutation {
    createProductSheet(
      description: "${description}",
      parts: [${partList}]
    ){
      ok
      errors{
        ...on PartNotFound{
          message
          errorType
        }
      }
      productSheet{${ProductSheetNode}}
    }
  }
  `;
  return fetch(`${root}/auth/v2/billing/graphql`, graphQLInit(graphQl))
    .then(response => response.json())
    .then(json => {
      if (json.errors) {
        failCallBack && failCallBack(apiFailCallbackHandle(json.errors));
      } else {
        const result = json.data.createProductSheet;
        if (result.ok) {
          dispatch({ type: ADMIN_UPDATE_PRODUCT_SHEET_LIST, productSheet: result.productSheet });
          doneCallback && doneCallback();
        } else {
          failCallBack && failCallBack(apiFailCallbackHandle(result.error));
        }
      }
    })
    .catch(err => {
      failCallBack && failCallBack(apiFailCallbackHandle(err.message));
    });
};

export const updateProductSheet = (
  id: string,
  description: string,
  parts: Array<*>,
  doneCallback: Function,
  failCallBack: Function
): * => (dispatch: DispatchType): * => {
  let partList = "";
  parts.map(
    (part: *): * => {
      let priceRanges = "";
      part.ranges.map(
        (range: *): * => {
          priceRanges +=
            "{ minimumQuantity:" + range.minimum + ", price:" + range.price + "},";
        }
      );

      partList +=
        "{ partNo:\"" +
        part.partNo +
        "\", basePrice:" +
        part.price +
        ", minimumQuantity:" +
        part.minimum +
        ", " +
        (part.maximum === null
          ? ""
          : "maximumQuantity:" + part.maximum + ", ") +
        "priceRanges: [" + priceRanges + "]" +
        "},";
    }
  );

  const graphQl = `
  mutation {
    updateProductSheet(
      id: "${id}",
      description: "${description}",
      parts: [${partList}]
    ){
      ok
      errors{
        ...on PartNotFound{
          message
          errorType
        }
        ...on ProductSheetNotFound{
          message
          errorType
        }
      }
      productSheet{${ProductSheetNode}}
    }
  }
  `;
  return fetch(`${root}/auth/v2/billing/graphql`, graphQLInit(graphQl))
    .then(response => response.json())
    .then(json => {
      if (json.errors) {
        failCallBack && failCallBack(apiFailCallbackHandle(json.errors));
      } else {
        const result = json.data.updateProductSheet;
        if (result.ok) {
          dispatch({ type: ADMIN_UPDATE_PRODUCT_SHEET_LIST, productSheet: result.productSheet });
          doneCallback && doneCallback();
        } else {
          failCallBack && failCallBack(apiFailCallbackHandle(result.error));
        }
      }
    })
    .catch(err => {
      failCallBack && failCallBack(apiFailCallbackHandle(err.message));
    });
};

export const fetchProductSheetList = (): * => (
  dispatch: DispatchType
): * => {
  const graphQl = `
  {
    productSheets {
      ${ProductSheetNode}
    }
  }
  `;
  return fetch(`${root}/auth/v2/billing/graphql`, graphQLInit(graphQl))
    .then(response => response.json())
    .then(json => {
      json.data.productSheets.forEach(productSheet => {
        dispatch({ type: ADMIN_UPDATE_PRODUCT_SHEET_LIST, productSheet: productSheet });
      });
    });
};


export const fetchSheetCompanies = (): * => (
  dispatch: DispatchType
): * => {
  const graphQl = `
  {
    productSheets {
      id
      description
      companies{
        edges{
          node{
            id
            uuid
            name
          }
        }
      }
    }
  }
  `;
  return fetch(`${root}/auth/v2/billing/graphql`, graphQLInit(graphQl))
    .then(response => response.json())
    .then(json => {
      dispatch({ type: ADMIN_GET_SHEET_COMPANY_LIST, productSheets: json.data.productSheets });
    });
};

export const createTier = (
  name: string,
  authorisedDistributer: string,
  authorisedReseller: string,
  standardDistributer: string,
  standardReseller: string,
  doneCallback: Function,
  failCallBack: Function
): * => (dispatch: DispatchType): * => {
  const graphQl = `
  mutation {
    createTier(
      name: "${name}",
      discounts: {
        standard: {
          reseller: ${standardReseller},
          distributer: ${standardDistributer}
        },
        authorised: {
          reseller: ${authorisedReseller},
          distributer: ${authorisedDistributer}
        }
      }
    ){
      ok
      tier{ ${DiscountsNode} }
    }
  }
  `;
  return fetch(`${root}/auth/v2/billing/graphql`, graphQLInit(graphQl))
    .then(response => response.json())
    .then(json => {
      if (json.errors) {
        failCallBack && failCallBack(apiFailCallbackHandle(json.errors));
      } else {
        const result = json.data.createTier;
        if (result.ok) {
          dispatch({ type: ADMIN_UPDATE_PRODUCT_TIER, tier: result.tier });
          doneCallback && doneCallback();
        } else {
          failCallBack && failCallBack(apiFailCallbackHandle(result.error));
        }
      }
    })
    .catch(err => {
      failCallBack && failCallBack(apiFailCallbackHandle(err.message));
    });
};

export const updateTier = (
  id: string,
  name: string,
  authorisedDistributer: string,
  authorisedReseller: string,
  standardDistributer: string,
  standardReseller: string,
  doneCallback: Function,
  failCallBack: Function
): * => (dispatch: DispatchType): * => {
  const graphQl = `
  mutation {
    updateTier(
      id: "${id}",
      name: "${name}",
      discounts: {
        standard: {
          reseller: ${standardReseller},
          distributer: ${standardDistributer}
        },
        authorised: {
          reseller: ${authorisedReseller},
          distributer: ${authorisedDistributer}
        }
      }
    ){
      ok
      tier{ ${DiscountsNode} }
    }
  }
  `;
  return fetch(`${root}/auth/v2/billing/graphql`, graphQLInit(graphQl))
    .then(response => response.json())
    .then(json => {
      if (json.errors) {
        failCallBack && failCallBack(apiFailCallbackHandle(json.errors));
      } else {
        const result = json.data.updateTier;
        if (result.ok) {
          dispatch({ type: ADMIN_UPDATE_PRODUCT_TIER, tier: result.tier });
          doneCallback && doneCallback();
        } else {
          failCallBack && failCallBack(apiFailCallbackHandle(result.error));
        }
      }
    })
    .catch(err => {
      failCallBack && failCallBack(apiFailCallbackHandle(err.message));
    });
};
